import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronRight, DollarSign, Percent, Calendar } from 'lucide-react';
import PercentageBar from '../PercentageBar';
import { formatCurrency, formatFactorRate } from '../../lib/formatters';

// localStorage key for persisting column widths
const COLUMN_WIDTHS_KEY = 'deals_table_column_widths';

// Default column widths
const DEFAULT_COLUMN_WIDTHS = {
  checkbox: 48,
  deal_id: 70,
  deal_shortcode: 100,
  deal_name: 180,
  merchant: 160,
  funding_date: 110,
  last_payment_date: 110,
  funding_amount: 110,
  payback: 100,
  balance: 100,
  balance_with_fees: 120,
  percent_paid_thru: 100,
  status: 110,
  actions: 100
};

// Sort icon component - defined outside
const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig.key !== column) {
    return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
  }
  return sortConfig.direction === 'asc' 
    ? <ArrowUp className="h-4 w-4 ml-1" />
    : <ArrowDown className="h-4 w-4 ml-1" />;
};

// Resizable column header component
const ResizableHeader = ({ children, columnKey, width, onResize, className, onClick }) => {
  const headerRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    
    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startXRef.current;
      const newWidth = Math.max(50, startWidthRef.current + diff);
      onResize(columnKey, newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [columnKey, width, onResize]);

  return (
    <th 
      ref={headerRef}
      className={`relative ${className}`}
      style={{ width: `${width}px`, minWidth: `${width}px` }}
      onClick={onClick}
    >
      {children}
      <div
        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 group"
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-gray-300 group-hover:bg-blue-500" />
      </div>
    </th>
  );
};

const DealsTable = ({
  deals,
  selectedDeals,
  onSelectDeal,
  onSelectAll,
  onEdit,
  onDelete,
  onRowClick,
  isAdmin,
  getStatusBadgeColor
}) => {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: 'deal_id', direction: 'desc' });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const allSelected = deals.length > 0 && selectedDeals.length === deals.length;

  // Toggle expanded row for non-admin users
  const toggleRowExpand = (dealId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dealId)) {
        newSet.delete(dealId);
      } else {
        newSet.add(dealId);
      }
      return newSet;
    });
  };

  // Handle row click - admin goes to detail page, others toggle expand
  const handleRowClick = (deal) => {
    if (isAdmin) {
      onRowClick && onRowClick(deal);
    } else {
      toggleRowExpand(deal.id);
    }
  };

  // Load column widths from localStorage or use defaults
  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(COLUMN_WIDTHS_KEY);
      if (saved) {
        return { ...DEFAULT_COLUMN_WIDTHS, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Error loading column widths:', e);
    }
    return DEFAULT_COLUMN_WIDTHS;
  });

  // Save column widths to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(COLUMN_WIDTHS_KEY, JSON.stringify(columnWidths));
    } catch (e) {
      console.error('Error saving column widths:', e);
    }
  }, [columnWidths]);

  // Handle column resize
  const handleResize = useCallback((columnKey, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: newWidth
    }));
  }, []);

  // Handle sort column click
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Navigate to contact page when clicking shortcode
  const handleShortcodeClick = (e, deal) => {
    e.stopPropagation();
    if (deal.company_id) {
      navigate(`/contacts/${deal.company_id}`);
    }
  };

  // Sort deals based on current sort configuration
  const sortedDeals = useMemo(() => {
    const sorted = [...deals].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      
      // Handle date columns
      if (sortConfig.key === 'funding_date' || sortConfig.key === 'last_payment_date') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      
      // Handle numeric columns (but not deal_id which can be alphanumeric)
      if (['funding_amount', 'payback', 'balance', 'percent_paid_thru', 'balance_with_fees'].includes(sortConfig.key)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      // Handle deal_id - numeric deals first, then alphanumeric at bottom
      if (sortConfig.key === 'deal_id') {
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        const aIsNumeric = !isNaN(aNum) && String(aVal).match(/^\d+$/);
        const bIsNumeric = !isNaN(bNum) && String(bVal).match(/^\d+$/);
        
        if (aIsNumeric && bIsNumeric) {
          // Both numeric — sort by value
          aVal = aNum;
          bVal = bNum;
        } else if (aIsNumeric && !bIsNumeric) {
          // Numeric always comes first (regardless of asc/desc direction)
          return sortConfig.direction === 'desc' ? -1 : -1;
        } else if (!aIsNumeric && bIsNumeric) {
          return sortConfig.direction === 'desc' ? 1 : 1;
        } else {
          // Both alphanumeric — sort as strings
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        }
      }
      
      // Handle string columns
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [deals, sortConfig]);

  // Format date for display — timezone-safe (08/05 Josh): dates are stored as
  // midnight timestamps; new Date().toLocaleDateString() shifts them back a
  // day in US timezones. Render the calendar date exactly as stored.
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const m = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
      return new Date(dateString).toLocaleDateString();
    } catch {
      return '-';
    }
  };

  return (
    <div className="relative w-full">
      <table className="w-full caption-bottom text-sm" style={{ tableLayout: 'fixed' }}>
        <thead className="sticky top-0 z-20 [&_tr]:border-b">
          <tr className="bg-blue-50 [&>th]:bg-blue-50 shadow-[0_1px_3px_rgba(0,0,0,0.1)] border-b transition-colors">
            {/* Expand column for non-admin users */}
            {!isAdmin && (
              <th className="w-10 p-2"></th>
            )}
            {isAdmin && (
              <ResizableHeader
                columnKey="checkbox"
                width={columnWidths.checkbox}
                onResize={handleResize}
                className="p-2"
              >
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={onSelectAll}
                />
              </ResizableHeader>
            )}
            <ResizableHeader 
              columnKey="deal_id"
              width={columnWidths.deal_id}
              onResize={handleResize}
              className="cursor-pointer hover:bg-gray-100 select-none p-2"
              onClick={() => handleSort('deal_id')}
            >
              <div className="flex items-center">
                ID
                <SortIcon column="deal_id" sortConfig={sortConfig} />
              </div>
            </ResizableHeader>
            <ResizableHeader 
              columnKey="deal_shortcode"
              width={columnWidths.deal_shortcode}
              onResize={handleResize}
              className="cursor-pointer hover:bg-gray-100 select-none p-2"
              onClick={() => handleSort('deal_shortcode')}
            >
              <div className="flex items-center">
                Shortcode
                <SortIcon column="deal_shortcode" sortConfig={sortConfig} />
              </div>
            </ResizableHeader>
            <ResizableHeader 
              columnKey="deal_name"
              width={columnWidths.deal_name}
              onResize={handleResize}
              className="cursor-pointer hover:bg-gray-100 select-none p-2"
              onClick={() => handleSort('deal_name')}
            >
              <div className="flex items-center">
                Deal Name
                <SortIcon column="deal_name" sortConfig={sortConfig} />
              </div>
            </ResizableHeader>
            <ResizableHeader 
              columnKey="merchant"
              width={columnWidths.merchant}
              onResize={handleResize}
              className="cursor-pointer hover:bg-gray-100 select-none p-2"
              onClick={() => handleSort('merchant')}
            >
              <div className="flex items-center">
                Merchant
                <SortIcon column="merchant" sortConfig={sortConfig} />
              </div>
            </ResizableHeader>
            <ResizableHeader 
              columnKey="funding_date"
              width={columnWidths.funding_date}
              onResize={handleResize}
              className="cursor-pointer hover:bg-gray-100 select-none p-2"
              onClick={() => handleSort('funding_date')}
            >
              <div className="flex items-center">
                Funded Date
                <SortIcon column="funding_date" sortConfig={sortConfig} />
              </div>
            </ResizableHeader>
            <ResizableHeader 
              columnKey="last_payment_date"
              width={columnWidths.last_payment_date}
              onResize={handleResize}
              className="cursor-pointer hover:bg-gray-100 select-none p-2"
              onClick={() => handleSort('last_payment_date')}
            >
              <div className="flex items-center">
                Last Payment
                <SortIcon column="last_payment_date" sortConfig={sortConfig} />
              </div>
            </ResizableHeader>
            <ResizableHeader 
              columnKey="funding_amount"
              width={columnWidths.funding_amount}
              onResize={handleResize}
              className="cursor-pointer hover:bg-gray-100 select-none p-2"
              onClick={() => handleSort('funding_amount')}
            >
              <div className="flex items-center">
                Funding
                <SortIcon column="funding_amount" sortConfig={sortConfig} />
              </div>
            </ResizableHeader>
            <ResizableHeader 
              columnKey="payback"
              width={columnWidths.payback}
              onResize={handleResize}
              className="cursor-pointer hover:bg-gray-100 select-none p-2"
              onClick={() => handleSort('payback')}
            >
              <div className="flex items-center">
                Payback
                <SortIcon column="payback" sortConfig={sortConfig} />
              </div>
            </ResizableHeader>
            <ResizableHeader 
              columnKey="balance"
              width={columnWidths.balance}
              onResize={handleResize}
              className="cursor-pointer hover:bg-gray-100 select-none p-2"
              onClick={() => handleSort('balance')}
            >
              <div className="flex items-center">
                Balance
                <SortIcon column="balance" sortConfig={sortConfig} />
              </div>
            </ResizableHeader>
            <ResizableHeader 
              columnKey="balance_with_fees"
              width={columnWidths.balance_with_fees}
              onResize={handleResize}
              className="cursor-pointer hover:bg-gray-100 select-none p-2"
              onClick={() => handleSort('balance_with_fees')}
            >
              <div className="flex items-center">
                Balance W/ Fees
                <SortIcon column="balance_with_fees" sortConfig={sortConfig} />
              </div>
            </ResizableHeader>
            <ResizableHeader 
              columnKey="percent_paid_thru"
              width={columnWidths.percent_paid_thru}
              onResize={handleResize}
              className="cursor-pointer hover:bg-gray-100 select-none p-2"
              onClick={() => handleSort('percent_paid_thru')}
            >
              <div className="flex items-center">
                % Paid
                <SortIcon column="percent_paid_thru" sortConfig={sortConfig} />
              </div>
            </ResizableHeader>
            <ResizableHeader 
              columnKey="status"
              width={columnWidths.status}
              onResize={handleResize}
              className="cursor-pointer hover:bg-gray-100 select-none p-2"
              onClick={() => handleSort('status')}
            >
              <div className="flex items-center">
                Status
                <SortIcon column="status" sortConfig={sortConfig} />
              </div>
            </ResizableHeader>
            {isAdmin && (
              <ResizableHeader
                columnKey="actions"
                width={columnWidths.actions}
                onResize={handleResize}
                className="p-2"
              >
                Actions
              </ResizableHeader>
            )}
          </tr>
        </thead>
        <TableBody>
          {sortedDeals.length === 0 ? (
            <TableRow>
              <TableCell colSpan={isAdmin ? 13 : 12} className="text-center py-8 text-gray-500">
                No deals found
              </TableCell>
            </TableRow>
          ) : (
            sortedDeals.map((deal) => (
              <React.Fragment key={deal.id}>
                <TableRow
                  className={`cursor-pointer hover:bg-gray-50 ${deal.status === 'Cancelled' ? 'line-through opacity-60' : ''}`}
                  onClick={() => handleRowClick(deal)}
                >
                  {/* Expand icon for non-admin users */}
                  {!isAdmin && (
                    <TableCell 
                      className="w-10 p-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRowExpand(deal.id);
                      }}
                    >
                      {expandedRows.has(deal.id) ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                      )}
                    </TableCell>
                  )}
                  {isAdmin && (
                    <TableCell 
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: `${columnWidths.checkbox}px` }}
                    >
                      <Checkbox
                        checked={selectedDeals.includes(deal.id)}
                        onCheckedChange={() => onSelectDeal(deal.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell 
                    className="font-medium"
                    style={{ width: `${columnWidths.deal_id}px` }}
                  >
                    {deal.deal_id}
                  </TableCell>
                  <TableCell style={{ width: `${columnWidths.deal_shortcode}px` }}>
                    {deal.company_id && isAdmin ? (
                      <button
                        onClick={(e) => handleShortcodeClick(e, deal)}
                        className="text-blue-600 font-medium hover:text-blue-800 hover:underline"
                        title="View Contact"
                      >
                        {deal.deal_shortcode || '-'}
                      </button>
                    ) : (
                      <span className="text-blue-600 font-medium">
                        {deal.deal_shortcode || '-'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell 
                    className="truncate"
                    style={{ width: `${columnWidths.deal_name}px` }}
                    title={deal.deal_name}
                  >
                    {deal.deal_name}
                  </TableCell>
                  <TableCell 
                    className="truncate"
                    style={{ width: `${columnWidths.merchant}px` }}
                    title={deal.merchant}
                  >
                    {deal.merchant || '-'}
                  </TableCell>
                  <TableCell style={{ width: `${columnWidths.funding_date}px` }}>
                    {formatDate(deal.funding_date)}
                  </TableCell>
                  <TableCell style={{ width: `${columnWidths.last_payment_date}px` }}>
                    {deal.last_payment_date ? formatDate(deal.last_payment_date) : <span className="text-gray-400">-</span>}
                  </TableCell>
                  <TableCell style={{ width: `${columnWidths.funding_amount}px` }}>
                    {formatCurrency(deal.funding_amount)}
                  </TableCell>
                  <TableCell style={{ width: `${columnWidths.payback}px` }}>
                    {formatCurrency(deal.payback)}
                  </TableCell>
                  <TableCell style={{ width: `${columnWidths.balance}px` }}>
                    {formatCurrency(deal.balance)}
                  </TableCell>
                  <TableCell 
                    className={`font-medium ${(() => { const v = deal.balance_with_fees ?? deal.balance ?? 0; return Math.abs(v) < 0.01 || v <= 0 ? 'text-green-600' : 'text-red-600'; })()}`}
                    style={{ width: `${columnWidths.balance_with_fees}px` }}
                  >
                    {formatCurrency(Math.abs(deal.balance_with_fees ?? deal.balance ?? 0) < 0.01 ? 0 : (deal.balance_with_fees ?? deal.balance ?? 0))}
                  </TableCell>
                  <TableCell style={{ width: `${columnWidths.percent_paid_thru}px` }}>
                    <PercentageBar percentage={deal.percent_paid_thru || 0} status={deal.status} />
                  </TableCell>
                  <TableCell style={{ width: `${columnWidths.status}px` }}>
                    <Badge className={getStatusBadgeColor(deal.status)}>
                      {deal.status}
                    </Badge>
                  </TableCell>
                  {isAdmin && (
                    <TableCell 
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: `${columnWidths.actions}px` }}
                    >
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(deal)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(deal.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
                
                {/* Expanded row content for non-admin users */}
                {!isAdmin && expandedRows.has(deal.id) && (
                  <TableRow className="bg-blue-50 hover:bg-blue-50">
                    <TableCell colSpan={12} className="py-4 px-6">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 p-2 rounded-lg">
                            <DollarSign className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Funding Amount</p>
                            <p className="text-lg font-semibold text-gray-900">
                              {formatCurrency(deal.funding_amount)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="bg-green-100 p-2 rounded-lg">
                            <Percent className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Factor Rate</p>
                            <p className="text-lg font-semibold text-gray-900">
                              {deal.rate ? Number(deal.rate).toFixed(5) : '-'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="bg-purple-100 p-2 rounded-lg">
                            <Calendar className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Term (Days)</p>
                            <p className="text-lg font-semibold text-gray-900">
                              {deal.term_days || '-'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="bg-orange-100 p-2 rounded-lg">
                            <DollarSign className="h-5 w-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Daily Payment</p>
                            <p className="text-lg font-semibold text-gray-900">
                              {formatCurrency(deal.payment_amount)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))
          )}
        </TableBody>
      </table>
    </div>
  );
};

export default React.memo(DealsTable);
